import type { Sprachschluessel } from './sprache'

/**
 * Die Formelsprache der Kacheln (7.5 Abschnitt 3.3, R339–R342).
 *
 * **Sie ist Anzeige und nie Abschluss.** Sie liest ausschließlich Werte, die
 * ein festgeschriebener Lauf bereits trägt, und schreibt ihr Ergebnis
 * nirgendwohin: kein Bericht, kein Abschluss, kein Anhang, kein Export mit
 * Prüfcode. Deshalb entsteht hier **kein zweiter Rechenweg** — es entsteht
 * überhaupt kein Rechenweg. R146 bleibt für alles gültig, was einen Abschluss
 * erzeugt; erlaubt ist die Formel dort, wo ein Anwender sich seine eigene
 * Kennzahl auf sein eigenes Brett legt.
 *
 * **Was die Sprache nicht kennt, ist der wichtigere Teil der Aufzählung**
 * (R341): keine Bedingung, keine Schleife, keine Zuweisung, keine Variable,
 * keinen Zugriff auf eine andere Kachel, keinen Aufruf nach außen. Eine
 * Schwelle — „grün ab 30 %" — ist ein Feld des Kachelblattes und ausdrücklich
 * kein Teil des Ausdrucks: Eine Bedingung im Ausdruck ist der erste Schritt
 * zur Skriptebene, und sie ist es unbemerkt.
 *
 * **Fehler kommen als Schlüssel und nicht als Satz** (R163, R161): Die Sprache
 * liegt in `sprache/`, und ein Befund aus dieser Datei muss in beiden Sprachen
 * dasselbe sagen. Deshalb ist dieses Stück auch ohne Oberfläche prüfbar —
 * `Formel.test.ts` liest Schlüssel, keine deutschen Sätze.
 */

/* --- Was herauskommt ------------------------------------------------------ */

/** Ein Befund mit Ursache, Menge und Weg (R145) — als Schlüssel mit Werten. */
export type Formelbefund = {
  schluessel: Sprachschluessel
  werte?: Record<string, string>
}

export type Knoten =
  | { art: 'zahl'; wert: number }
  | { art: 'bezug'; schluessel: string }
  | { art: 'vorzeichen'; knoten: Knoten }
  | { art: 'rechnung'; zeichen: Rechenzeichen; links: Knoten; rechts: Knoten }
  | { art: 'funktion'; name: Funktionsname; argumente: Knoten[] }

export type Rechenzeichen = '+' | '-' | '*' | '/'

/**
 * Vier Funktionen, benannt und ohne Nebenwirkung (R341).
 *
 * Sie sind keine Bequemlichkeit, sondern die Grenze: Was hier nicht steht, gibt
 * es nicht — und eine fünfte ist eine Fachentscheidung wie eine vierte
 * Kachelart, keine Einstellung.
 */
export const FUNKTIONEN = ['abs', 'min', 'max', 'runde'] as const
export type Funktionsname = (typeof FUNKTIONEN)[number]

/** Ein Wert, auf den sich eine Formel beziehen kann. */
export type Bezug = {
  /** Der Name im Ausdruck, wie er in eckigen Klammern steht. */
  schluessel: string
  /** Die Beschriftung für Menschen — aus den Stammdaten, nicht aus dem Code. */
  name: string
  wert: number
  /** `null` bei Mengen (Anzahl Befunde, Millisekunden) — die haben keine. */
  waehrung: string | null
}

export type Bezugsraum = Record<string, Bezug>

/* --- Zerlegen ------------------------------------------------------------- */

type Zeichen =
  | { art: 'zahl'; wert: number; text: string }
  | { art: 'bezug'; schluessel: string; text: string }
  | { art: 'name'; text: string }
  | { art: 'zeichen'; text: string }

const TRENNER = ';'

/**
 * Zerlegt den Text in Zeichen.
 *
 * **Das Komma ist Dezimaltrennzeichen und nicht Argumenttrenner.** Ein
 * deutschsprachiger Anwender tippt `0,5`, und eine Formelsprache, die daraus
 * zwei Argumente macht, ist an der einen Stelle falsch, an der es um Zahlen
 * geht. Argumente trennt deshalb das Semikolon — dieselbe Wahl, die jede
 * deutschsprachige Tabellenkalkulation trifft.
 */
export function zerlege(text: string): Zeichen[] | Formelbefund {
  const raus: Zeichen[] = []
  let i = 0

  while (i < text.length) {
    const c = text.charAt(i)

    if (c === ' ' || c === '\t' || c === '\n') {
      i += 1
      continue
    }

    if (c === '[') {
      const ende = text.indexOf(']', i)
      if (ende < 0) {
        return { schluessel: 'formel.bezug.offen', werte: { text: text.slice(i + 1) } }
      }

      const schluessel = text.slice(i + 1, ende).trim()
      if (!schluessel) {
        return { schluessel: 'formel.bezug.leer' }
      }

      raus.push({ art: 'bezug', schluessel, text: `[${schluessel}]` })
      i = ende + 1
      continue
    }

    if (istZiffer(c)) {
      let j = i
      while (j < text.length && (istZiffer(text.charAt(j)) || text.charAt(j) === '.' || text.charAt(j) === ',')) {
        j += 1
      }

      const roh = text.slice(i, j)
      // Beide Schreibweisen gelten: Wer `0.5` tippt, meint keine 5.
      const wert = Number(roh.replace(',', '.'))
      if (!Number.isFinite(wert)) {
        return { schluessel: 'formel.zahl.unlesbar', werte: { text: roh } }
      }

      raus.push({ art: 'zahl', wert, text: roh })
      i = j
      continue
    }

    if (istBuchstabe(c)) {
      let j = i
      while (j < text.length && (istBuchstabe(text.charAt(j)) || istZiffer(text.charAt(j)))) {
        j += 1
      }

      raus.push({ art: 'name', text: text.slice(i, j) })
      i = j
      continue
    }

    if ('+-*/()'.includes(c) || c === TRENNER) {
      raus.push({ art: 'zeichen', text: c })
      i += 1
      continue
    }

    return { schluessel: 'formel.zeichen.unbekannt', werte: { zeichen: c } }
  }

  return raus
}

function istZiffer(c: string): boolean {
  return c >= '0' && c <= '9'
}

function istBuchstabe(c: string): boolean {
  return /[a-zA-ZäöüÄÖÜß_.]/.test(c)
}

/* --- Lesen ---------------------------------------------------------------- */

export type Lesung = { knoten: Knoten } | { fehler: Formelbefund }

export function istFehler(lesung: Lesung): lesung is { fehler: Formelbefund } {
  return 'fehler' in lesung
}

/**
 * Liest den Ausdruck — Punkt vor Strich, Klammern, Vorzeichen.
 *
 * Ein absteigender Leser von Hand und kein erzeugter: Die Sprache hat elf
 * Regeln, und eine Werkzeugkette dafür wäre mehr Abhängigkeit als Gewinn
 * (ADR 005 im Geist — was die Anwendung nicht braucht, lädt sie nicht).
 */
export function lies(text: string): Lesung {
  const zeichen = zerlege(text)
  if (!Array.isArray(zeichen)) {
    return { fehler: zeichen }
  }

  if (zeichen.length === 0) {
    return { fehler: { schluessel: 'formel.leer' } }
  }

  const stand = { i: 0 }
  const ergebnis = summe(zeichen, stand)
  if (istFehler(ergebnis)) {
    return ergebnis
  }

  const ueberhang = bei(zeichen, stand.i)
  if (ueberhang) {
    return {
      fehler: { schluessel: 'formel.ueberhang', werte: { text: ueberhang.text } },
    }
  }

  return ergebnis
}

type Stand = { i: number }

/** Das Zeichen an einer Stelle — `null` hinter dem Ende, statt `undefined`. */
function bei(z: readonly Zeichen[], i: number): Zeichen | null {
  return z[i] ?? null
}

/** Steht hier eines dieser Rechenzeichen? */
function rechenzeichen(z: readonly Zeichen[], i: number, welche: string[]): Rechenzeichen | null {
  const jetzt = bei(z, i)
  return jetzt && jetzt.art === 'zeichen' && welche.includes(jetzt.text)
    ? (jetzt.text as Rechenzeichen)
    : null
}

function summe(z: Zeichen[], s: Stand): Lesung {
  let links = produkt(z, s)
  if (istFehler(links)) {
    return links
  }

  for (;;) {
    const zeichen = rechenzeichen(z, s.i, ['+', '-'])
    if (!zeichen) {
      break
    }

    s.i += 1
    const rechts = produkt(z, s)
    if (istFehler(rechts)) {
      return rechts
    }

    links = { knoten: { art: 'rechnung', zeichen, links: links.knoten, rechts: rechts.knoten } }
  }

  return links
}

function produkt(z: Zeichen[], s: Stand): Lesung {
  let links = glied(z, s)
  if (istFehler(links)) {
    return links
  }

  for (;;) {
    const zeichen = rechenzeichen(z, s.i, ['*', '/'])
    if (!zeichen) {
      break
    }

    s.i += 1
    const rechts = glied(z, s)
    if (istFehler(rechts)) {
      return rechts
    }

    links = { knoten: { art: 'rechnung', zeichen, links: links.knoten, rechts: rechts.knoten } }
  }

  return links
}

function glied(z: Zeichen[], s: Stand): Lesung {
  const vorher = bei(z, s.i - 1)?.text ?? null
  const jetzt = bei(z, s.i)

  if (!jetzt) {
    return {
      fehler: vorher
        ? { schluessel: 'formel.operand.fehlt', werte: { zeichen: vorher } }
        : { schluessel: 'formel.leer' },
    }
  }

  if (jetzt.art === 'zahl') {
    s.i += 1
    return { knoten: { art: 'zahl', wert: jetzt.wert } }
  }

  if (jetzt.art === 'bezug') {
    s.i += 1
    return { knoten: { art: 'bezug', schluessel: jetzt.schluessel } }
  }

  if (jetzt.art === 'name') {
    return funktion(z, s)
  }

  if (jetzt.text === '-') {
    s.i += 1
    const innen = glied(z, s)
    if (istFehler(innen)) {
      return innen
    }

    return { knoten: { art: 'vorzeichen', knoten: innen.knoten } }
  }

  if (jetzt.text === '(') {
    s.i += 1
    const innen = summe(z, s)
    if (istFehler(innen)) {
      return innen
    }

    if (bei(z, s.i)?.text !== ')') {
      return { fehler: { schluessel: 'formel.klammer.zu' } }
    }

    s.i += 1
    return innen
  }

  if (jetzt.text === ')') {
    return { fehler: { schluessel: 'formel.klammer.auf' } }
  }

  return { fehler: { schluessel: 'formel.operand.fehlt', werte: { zeichen: vorher ?? jetzt.text } } }
}

/** Wie viele Werte jede Funktion erwartet — `null` heißt „zwei oder mehr". */
const STELLIGKEIT: Record<Funktionsname, number | null> = {
  abs: 1,
  runde: 2,
  min: null,
  max: null,
}

function funktion(z: Zeichen[], s: Stand): Lesung {
  const geschrieben = bei(z, s.i)?.text ?? ''
  const name = geschrieben.toLowerCase()

  if (!(FUNKTIONEN as readonly string[]).includes(name)) {
    return {
      fehler: {
        schluessel: 'formel.funktion.unbekannt',
        werte: { name: geschrieben, vorrat: FUNKTIONEN.join(', ') },
      },
    }
  }

  s.i += 1

  if (bei(z, s.i)?.text !== '(') {
    return { fehler: { schluessel: 'formel.funktion.klammer', werte: { name } } }
  }

  s.i += 1
  const argumente: Knoten[] = []

  // Die leere Argumentliste ist ein eigener Fall: `min()` ist kein Ausdruck,
  // sondern eine vergessene Eingabe, und sie bekommt ihren eigenen Satz.
  if (bei(z, s.i)?.text === ')') {
    return { fehler: stelligkeitsbefund(name as Funktionsname, 0) }
  }

  for (;;) {
    const wert = summe(z, s)
    if (istFehler(wert)) {
      return wert
    }

    argumente.push(wert.knoten)

    if (bei(z, s.i)?.text === TRENNER) {
      s.i += 1
      continue
    }

    break
  }

  if (bei(z, s.i)?.text !== ')') {
    return { fehler: { schluessel: 'formel.klammer.zu' } }
  }

  s.i += 1

  const erwartet = STELLIGKEIT[name as Funktionsname]
  const passt = erwartet === null ? argumente.length >= 2 : argumente.length === erwartet
  if (!passt) {
    return { fehler: stelligkeitsbefund(name as Funktionsname, argumente.length) }
  }

  return { knoten: { art: 'funktion', name: name as Funktionsname, argumente } }
}

/**
 * „erwartet zwei Werte" und „erwartet mindestens zwei" sind zwei Sätze.
 *
 * `min` nimmt beliebig viele, `abs` genau einen — wer beides in denselben Satz
 * presst, schreibt entweder für `abs` etwas Falsches oder für `min` etwas
 * Unverständliches.
 */
function stelligkeitsbefund(name: Funktionsname, tatsaechlich: number): Formelbefund {
  const erwartet = STELLIGKEIT[name]

  // Stelligkeiten sind kleine Anzahlen (unter tausend) und gehen als Text
  // in den Satz (R841 — In einen Satz wird nur Text eingesetzt).
  return erwartet === null
    ? { schluessel: 'formel.funktion.mindestens', werte: { name, erwartet: '2', tatsaechlich: String(tatsaechlich) } }
    : { schluessel: 'formel.funktion.stelligkeit', werte: { name, erwartet: String(erwartet), tatsaechlich: String(tatsaechlich) } }
}

/* --- Auswerten ------------------------------------------------------------ */

export type Auswertung = { wert: number } | { fehler: Formelbefund }

export function istAuswertungsfehler(a: Auswertung): a is { fehler: Formelbefund } {
  return 'fehler' in a
}

/** Welche Bezüge ein Ausdruck braucht — für den Abruf und für das Kachelblatt. */
export function bezuegeIn(knoten: Knoten): string[] {
  const raus: string[] = []

  const geh = (k: Knoten): void => {
    switch (k.art) {
      case 'bezug':
        if (!raus.includes(k.schluessel)) {
          raus.push(k.schluessel)
        }
        break
      case 'vorzeichen':
        geh(k.knoten)
        break
      case 'rechnung':
        geh(k.links)
        geh(k.rechts)
        break
      case 'funktion':
        k.argumente.forEach(geh)
        break
      default:
        break
    }
  }

  geh(knoten)
  return raus
}

/**
 * Rechnet aus — und weist zwei Fälle ausdrücklich ab.
 *
 * **Der Nenner null** ist keine Unendlichkeit, sondern eine Auskunft: Die
 * Kennzahl hat für diese Periode keinen Gegenstand. Und **zwei Währungen**
 * sind keine Zahl (7.5, Grenzfälle) — stillschweigend umrechnen hieße, einen
 * Kurs zu wählen, den niemand gesehen hat.
 */
export function werteAus(knoten: Knoten, raum: Bezugsraum): Auswertung {
  const fehlend = bezuegeIn(knoten).filter((b) => !(b in raum))
  if (fehlend.length > 0) {
    return { fehler: { schluessel: 'formel.bezug.unbekannt', werte: { bezug: fehlend[0] ?? '' } } }
  }

  const gemischt = waehrungsmischung(knoten, raum)
  if (gemischt) {
    return { fehler: gemischt }
  }

  return rechne(knoten, raum)
}

/** Die Währung des Ergebnisses — `null`, wo eine Rechnung sie verliert. */
export function waehrungVon(knoten: Knoten, raum: Bezugsraum): string | null {
  const waehrungen = bezuegeIn(knoten)
    .map((b) => raum[b]?.waehrung ?? null)
    .filter((w): w is string => w !== null)

  return waehrungen[0] ?? null
}

function waehrungsmischung(knoten: Knoten, raum: Bezugsraum): Formelbefund | null {
  const waehrungen = [
    ...new Set(
      bezuegeIn(knoten)
        .map((b) => raum[b]?.waehrung ?? null)
        .filter((w): w is string => w !== null),
    ),
  ]

  if (waehrungen.length < 2) {
    return null
  }

  return {
    schluessel: 'formel.waehrungen',
    werte: { eine: waehrungen[0] ?? '', andere: waehrungen[1] ?? '' },
  }
}

function rechne(k: Knoten, raum: Bezugsraum): Auswertung {
  switch (k.art) {
    case 'zahl':
      return { wert: k.wert }

    case 'bezug':
      return { wert: raum[k.schluessel]?.wert ?? 0 }

    case 'vorzeichen': {
      const innen = rechne(k.knoten, raum)
      return istAuswertungsfehler(innen) ? innen : { wert: -innen.wert }
    }

    case 'rechnung': {
      const links = rechne(k.links, raum)
      if (istAuswertungsfehler(links)) {
        return links
      }

      const rechts = rechne(k.rechts, raum)
      if (istAuswertungsfehler(rechts)) {
        return rechts
      }

      if (k.zeichen === '/' && rechts.wert === 0) {
        // **Der Nenner wird beim Namen genannt, wenn er einen hat.** „Der
        // Nenner ,Bilanzsumme' ist null" sagt dem Anwender, welche Zahl fehlt;
        // bei einer getippten Null wäre derselbe Satz eine Tautologie.
        return {
          fehler:
            k.rechts.art === 'bezug'
              ? {
                  schluessel: 'formel.nenner.null',
                  werte: { nenner: benenne(k.rechts, raum) },
                }
              : { schluessel: 'formel.nenner.null.ohne' },
        }
      }

      switch (k.zeichen) {
        case '+':
          return { wert: links.wert + rechts.wert }
        case '-':
          return { wert: links.wert - rechts.wert }
        case '*':
          return { wert: links.wert * rechts.wert }
        default:
          return { wert: links.wert / rechts.wert }
      }
    }

    case 'funktion': {
      const werte: number[] = []
      for (const a of k.argumente) {
        const einer = rechne(a, raum)
        if (istAuswertungsfehler(einer)) {
          return einer
        }

        werte.push(einer.wert)
      }

      switch (k.name) {
        case 'abs':
          return { wert: Math.abs(werte[0] ?? 0) }
        case 'min':
          return { wert: Math.min(...werte) }
        case 'max':
          return { wert: Math.max(...werte) }
        default: {
          const stellen = Math.max(0, Math.min(10, Math.trunc(werte[1] ?? 0)))
          const faktor = 10 ** stellen
          return { wert: Math.round((werte[0] ?? 0) * faktor) / faktor }
        }
      }
    }
  }
}

/* --- Zeigen --------------------------------------------------------------- */

/**
 * Die Rechnung mit eingesetzten Zahlen (R342).
 *
 * Das ist der Unterschied zwischen einer Kennzahl und einer Behauptung: Wer
 * `38,4 %` liest, sieht auf Abruf `1.920.000 / 5.000.000 * 100` — und jeder
 * Operand führt von dort in den Wertenachweis.
 */
export function eingesetzt(k: Knoten, raum: Bezugsraum, zahl: (wert: number) => string): string {
  switch (k.art) {
    case 'zahl':
      return zahl(k.wert)
    case 'bezug':
      const bezug = raum[k.schluessel]
      return bezug ? zahl(bezug.wert) : `[${k.schluessel}]`
    case 'vorzeichen':
      return `-${eingesetzt(k.knoten, raum, zahl)}`
    case 'rechnung':
      return `(${eingesetzt(k.links, raum, zahl)} ${k.zeichen} ${eingesetzt(k.rechts, raum, zahl)})`
    case 'funktion':
      return `${k.name}(${k.argumente.map((a) => eingesetzt(a, raum, zahl)).join(`${TRENNER} `)})`
  }
}

/** Der Name eines Teilausdrucks für eine Meldung — der Bezug, sonst der Ausdruck. */
function benenne(k: Knoten, raum: Bezugsraum): string {
  if (k.art === 'bezug') {
    return raum[k.schluessel]?.name ?? k.schluessel
  }

  return eingesetzt(k, raum, (w) => String(w))
}
